import { Download } from '@mui/icons-material';
import dayjs from 'dayjs';
import { uniq, uniqBy } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { Constants } from '../../../../../Core/Constants';
import { GetCustomerSettingsResponse, PositionModel, YearDataModel } from '../../../../../Generated/Commodity-Management-Api';
import { ContractDateHelper, downloadFile } from '../../../../../Helpers';
import { useLoadingState } from '../../../../../Hooks';
import { LoadingState } from '../../../../../Types';
import { StoneXLoading, StoneXRow } from '../../../../StoneX';
import { StoneXButton, StoneXSelectPills } from '../../../../StoneXMui';

interface BasicQueryFormProps {
  customerSettings: GetCustomerSettingsResponse;
  years: YearDataModel[];
  loadingStateSetter: (loadingState: LoadingState) => void;
  onPositionsUpdated: (positions: PositionModel[]) => void;
}

export default function BasicQueryForm(props: BasicQueryFormProps) {
  const { customerSettings, years, loadingStateSetter, onPositionsUpdated } = props;

  const positionDownloadingState = useLoadingState();

  const [positions, setPositions] = useState<PositionModel[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);

  const loadingState = useLoadingState();

  function downloadPositions() {
    if (selectedYears.length === 0) {
      enqueueSnackbar('No years selected for export.', { variant: 'error' });
      return;
    }

    const startYear = Math.min(...selectedYears);
    const endYear = Math.max(...selectedYears);

    const startDate = ContractDateHelper.getStartContractDate(startYear, customerSettings?.fiscalMonthOffset);
    const endDate = ContractDateHelper.addMonths(
      ContractDateHelper.getStartContractDate(endYear, customerSettings?.fiscalMonthOffset),
      11
    );

    CommodityManagementApi.getPositionExcelExport(
      positionDownloadingState.setLoadingState,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      startDate?.year,
      startDate?.month,
      undefined,
      endDate?.year,
      endDate?.month,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined, // keep the boolean parameter for backward compatibility
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { responseType: 'blob', headers: { 'Content-Type': Constants.MediaType.Excel } }
    ).then(downloadFile)
    .catch((error) => {
      enqueueSnackbar('Error downloading file', { variant: 'error' });
    });
  }

  function getInitalPositions() {
    const currentYear = dayjs().year();
    const startDate = ContractDateHelper.getStartContractDate(currentYear, customerSettings?.fiscalMonthOffset);

    CommodityManagementApi.listPositions(
      loadingState.setLoadingState,
      undefined, //mtmDate
      false, //include expiration dates
      false, //include market prices
      false, // don't use display units - use original units
      undefined, // no scenario market prices
      undefined, //commodityFilter,
      undefined, //locationFilter,
      undefined, //counterpartyFilter,
      undefined, //trade start date
      undefined, //trade end date
      startDate.year,
      startDate.month,
      undefined, //label (ignore)
    ).then((q) => {
      setPositions(q.data.rows ?? []);
      const years = uniq(q.data.rows?.map(x => x.budgetDate.year!));
      setSelectedYears(years);
    });
  }

  function removeYearPositions(year: number) {
    const startDate = ContractDateHelper.getStartContractDate(year, customerSettings?.fiscalMonthOffset);
    const endDate = ContractDateHelper.addMonths(startDate, 12);
    setSelectedYears(previous => previous.filter(x => x != year));
    setPositions(previous => previous.filter(x => !(ContractDateHelper.isGreaterThanOrEqualTo(x.budgetDate, startDate) && ContractDateHelper.isLessThanOrEqualTo(x.budgetDate, endDate))));
  }

  function addYearPositions(year: number) {
    const startMonth = ContractDateHelper.getStartContractDate(year, customerSettings?.fiscalMonthOffset);
    const endMonth = ContractDateHelper.addMonths(startMonth, 11);

    CommodityManagementApi.listPositions(
      loadingState.setLoadingState,
      undefined, //mtmDate
      false, //include expiration dates
      false, //include market prices
      false, // don't use display units - use original units
      undefined, // no scenario market prices
      undefined, //commodityFilter,
      undefined, //locationFilter,
      undefined, //counterpartyFilter,
      undefined, //trade start date
      undefined, //trade end date
      startMonth.year,
      startMonth.month,
      undefined, //label (ignore)
      endMonth.year,
      endMonth.month,
    ).then((q) => {
      setPositions(previous => uniqBy([...previous, ...q.data.rows ?? []], x => x.positionId))
      setSelectedYears(previous => [...previous, year]);
    });
  }

  useEffect(() => onPositionsUpdated(positions), [positions]);
  useEffect(getInitalPositions, []);

  return (
    <StoneXRow>
      <StoneXSelectPills 
          label="Fiscal Year" 
          options={years.map(x => x.year)} 
          multiple={true}
          getOptionId={x => x} 
          getOptionLabel={x => `${x}`}
          getSortingValue={x => x} 
          value={selectedYears}
          onSelect={addYearPositions}
          onDeselect={removeYearPositions}
      />
      
      <StoneXLoading show={loadingState.isLoading()}/>
      
      <StoneXButton className='pull-right' variant="outlined" onClick={downloadPositions} style={{ marginBottom: '10px' }} startIcon={<Download />} isWorking={positionDownloadingState.isLoading()}>
          Export Positions
      </StoneXButton>
    </StoneXRow>
  );
}